import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Target, CalendarClock, Users, Video, Plus, Search, Star, ArrowRight, Wallet,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { StatCard, PageHeader, UserAvatar, StatusBadge, EmptyState, Spinner, Pill, SectionCard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AddToCalendar } from "@/components/AddToCalendar";
import { toast } from "sonner";
import { fmtDate, fmtTime, fmtMoney } from "@/lib/constants";

function SessionRow({ b, onCancel, onFeedback }) {
  const { t } = useTranslation();
  const isUpcoming = ["confirmed", "pending"].includes(b.status);
  return (
    <div data-testid={`session-${b.booking_id}`} className="flex items-center gap-3.5 rounded-xl border border-border p-4 transition-colors hover:border-primary/30 hover:bg-secondary/30">
      <UserAvatar src={fileUrl(b.expert_picture)} name={b.expert_name} size={44} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{b.expert_name}</p>
          <StatusBadge status={b.status} />
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{b.topic}</p>
        <p className="text-xs text-muted-foreground mt-1">{fmtDate(b.start)} · {fmtTime(b.start)}</p>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        {b.status === "confirmed" && b.meeting_link && (
          <a href={b.meeting_link} target="_blank" rel="noreferrer" data-testid={`join-${b.booking_id}`}>
            <Button size="sm" className="rounded-lg gap-1.5"><Video size={14} /> {t("emp.dashboard.join")}</Button>
          </a>
        )}
        {b.status === "confirmed" && <AddToCalendar booking={b} />}
        {isUpcoming && <button onClick={() => onCancel(b)} className="text-xs text-muted-foreground hover:text-destructive">{t("emp.dashboard.cancel")}</button>}
        {b.status === "completed" && !b.feedback && (
          <Button size="sm" variant="outline" className="rounded-lg gap-1.5" data-testid={`feedback-${b.booking_id}`} onClick={() => onFeedback(b)}><Star size={14} /> {t("emp.dashboard.rate")}</Button>
        )}
        {b.feedback && <span className="inline-flex items-center gap-1 text-xs text-warning"><Star size={13} className="fill-warning" /> {b.feedback.rating}.0</span>}
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [fb, setFb] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/employee/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const cancel = async (b) => {
    try { await api.post(`/bookings/${b.booking_id}/cancel`); toast.success(t("emp.dashboard.toastCancelled")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submitFeedback = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${fb.booking_id}/feedback`, { rating, comment }); toast.success(t("emp.dashboard.toastThanks")); setFb(null); setComment(""); setRating(5); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!d) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  const approved = d.approved_needs || [];
  const companyName = d.company_name || t("emp.eyebrow");
  const bud = d.budget;
  const hasBudget = bud && bud.has_allocation;

  return (
    <AppShell>
      <PageHeader eyebrow={d.company_name || t("emp.eyebrow")} title={t("emp.dashboard.greeting", { name: user?.name?.split(" ")[0] })} description={t("emp.dashboard.subtitle", { company: companyName })}>
        <Link to="/experts"><Button data-testid="find-expert-cta" className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><Search size={16} /> {t("emp.dashboard.findExpert")}</Button></Link>
      </PageHeader>

      <div className={`grid grid-cols-2 ${hasBudget ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
        <div className="animate-fade-up d1"><StatCard icon={Target} label={t("emp.dashboard.approvedGoals")} value={approved.length} sub={approved.length ? t("emp.dashboard.approvedReady") : t("emp.dashboard.approvedAwaiting")} accent /></div>
        <div className="animate-fade-up d2"><StatCard icon={CalendarClock} label={t("emp.dashboard.upcomingSessions")} value={d.upcoming.length} sub={d.upcoming.length ? t("emp.dashboard.upcomingScheduled") : t("emp.dashboard.upcomingNone")} /></div>
        <div className="animate-fade-up d3"><StatCard icon={Users} label={t("emp.dashboard.expertsAvailable")} value={d.network_size} sub={t("emp.dashboard.inYourNetwork")} /></div>
        {hasBudget && (
          <div className="animate-fade-up d4" data-testid="emp-budget-card">
            <StatCard icon={Wallet} label={t("emp.dashboard.budgetLabel")} value={fmtMoney(bud.remaining, bud.currency)} sub={t("emp.dashboard.budgetSub", { used: fmtMoney(bud.used, bud.currency), total: fmtMoney(bud.allocated, bud.currency) })} />
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard icon={CalendarClock} title={t("emp.dashboard.upcomingSessions")} description={t("emp.dashboard.yourCoaching")} count={d.upcoming.length} className="animate-fade-up d2">
            {d.upcoming.length ? (
              <div className="space-y-3">{d.upcoming.map((b) => <SessionRow key={b.booking_id} b={b} onCancel={cancel} onFeedback={setFb} />)}</div>
            ) : (
              <EmptyState icon={CalendarClock} title={t("emp.dashboard.noUpcoming")} description={approved.length ? t("emp.dashboard.noUpcomingBook") : t("emp.dashboard.noUpcomingGoal")}
                action={<Link to={approved.length ? "/experts" : "/app/goals"}><Button variant="outline" className="rounded-lg gap-1.5">{approved.length ? t("emp.dashboard.findExpert") : t("emp.dashboard.createGoal")} <ArrowRight size={15} /></Button></Link>} />
            )}
          </SectionCard>

          {d.past.length > 0 && (
            <SectionCard icon={Star} title={t("emp.dashboard.pastSessions")} description={t("emp.dashboard.pastCompleted")} className="animate-fade-up d3">
              <div className="space-y-3">{d.past.map((b) => <SessionRow key={b.booking_id} b={b} onCancel={cancel} onFeedback={setFb} />)}</div>
            </SectionCard>
          )}
        </div>

        <SectionCard
          icon={Target}
          title={t("emp.dashboard.yourGoals")}
          description={t("emp.dashboard.yourGoalsSub")}
          className="animate-fade-up d4 h-fit"
          action={<Link to="/app/goals"><button className="grid place-items-center w-8 h-8 rounded-lg text-primary hover:bg-primary/10 transition-colors" data-testid="add-goal-icon"><Plus size={17} /></button></Link>}
        >
          {(d.needs || []).length ? (
            <div className="space-y-3">
              {d.needs.map((n) => (
                <div key={n.need_id} className="rounded-xl border border-border p-3.5 transition-colors hover:border-primary/30">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium flex-1 truncate">{n.title}</p>
                    <StatusBadge status={n.status} />
                  </div>
                  <Pill className="bg-primary/10 text-primary mt-2">{n.field}</Pill>
                  {n.status === "approved" && (
                    <Link to="/experts"><Button size="sm" className="w-full mt-3 rounded-lg gap-1.5" data-testid={`book-for-${n.need_id}`}>{t("emp.dashboard.bookExpert")} <ArrowRight size={14} /></Button></Link>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground mb-3">{t("emp.dashboard.noGoals")}</p>
              <Link to="/app/goals"><Button variant="outline" size="sm" className="rounded-lg gap-1.5"><Plus size={14} /> {t("emp.dashboard.defineGoal")}</Button></Link>
            </div>
          )}
        </SectionCard>
      </div>

      <Dialog open={!!fb} onOpenChange={(o) => !o && setFb(null)}>
        <DialogContent className="rounded-[0.7rem]">
          <DialogHeader><DialogTitle>{t("emp.dashboard.feedbackTitle", { name: fb?.expert_name })}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-1 justify-center py-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} data-testid={`rate-${i}`} onClick={() => setRating(i)} className="p-1">
                <Star size={30} className={i <= rating ? "fill-warning text-warning" : "text-border"} />
              </button>
            ))}
          </div>
          <Textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("emp.dashboard.feedbackPlaceholder")} className="rounded-lg min-h-24" />
          <DialogFooter>
            <Button data-testid="submit-feedback" onClick={submitFeedback} disabled={busy} className="rounded-lg">{busy ? <Spinner /> : t("emp.dashboard.submitFeedback")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
